import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  AdjustmentsHorizontalIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
  ArrowRightOnRectangleIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/solid';
import React, { Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';
import newslitLogo from '../../assets/images/newslit-logo.svg';
import { usePwaContext } from '../../contexts/pwa';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { Account } from '../../services/models/account';
import { Brief } from '../../services/models/brief';
import { ExternalUserType } from '../../services/models/external-user';
import { FeatureFlag } from '../../services/models/feature-flags';
import { classNames } from '../../utils/class-names';
import { AddNewBriefBtn } from '../add-new-brief-btn/addNewBriefBtn';
import { EmptyBriefCTA } from '../empty-brief-cta/emptyBriefCTA';
import { UpsellSidebarNotification } from '../upsell-sidebar-notification/upsellSidebarNotification';

interface DesktopSidebarProps {
  account: Account;
  briefs: Brief[];
  currentBriefCount: number;
  maxBriefCount: number;
  onAddNewBrief: () => void;
  onTwitterSignInSuccess: () => void;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  account,
  briefs,
  currentBriefCount,
  maxBriefCount,
  onAddNewBrief,
  onTwitterSignInSuccess,
}) => {
  const { deferredPrompt, isPwaCompatibleBrowser } = usePwaContext();

  const socialBriefFlag = useFeatureFlag(FeatureFlag.ShowSocialBrief);

  const twitterUser = account.externalUsers.find(eu => eu.type === ExternalUserType.TWITTER);

  return (
    <div className="flex flex-col w-64">
      <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex items-center h-12 shrink-0 px-4">
            <img className="h-7 w-auto" src={newslitLogo} alt="Newslit" />
          </div>

          <div className="h-0 flex-1 flex flex-col overflow-y-auto">
            {/* Navigation */}
            <nav className="px-2 pt-4">
              <div className="mt-6">
                {/* Secondary navigation */}
                <div className="flex items-center justify-between">
                  <h3
                    className="px-3 text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    id="desktop-briefs-headline"
                  >
                    Briefs
                  </h3>
                  <span className="font-light text-gray-700 tracking-wider">
                    using <span className="font-bold text-gray-900">{currentBriefCount}</span>/
                    {maxBriefCount}
                  </span>
                </div>

                {currentBriefCount === 0 &&
                  (!account.metadata?.isTwitterUser || !socialBriefFlag) && (
                    <div className="pt-4">
                      <EmptyBriefCTA onClick={onAddNewBrief} />
                    </div>
                  )}

                {((socialBriefFlag && account.metadata?.isTwitterUser) ||
                  currentBriefCount > 0) && (
                  <div className="py-4">
                    <AddNewBriefBtn onClick={onAddNewBrief} />
                  </div>
                )}

                <div
                  className="mt-1 space-y-1"
                  role="group"
                  aria-labelledby="desktop-briefs-headline"
                >
                  {socialBriefFlag && account.metadata?.isTwitterUser && twitterUser!! && (
                    <NavLink
                      to="/dashboard/social"
                      className="group flex items-center px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                      activeClassName="bg-gray-100 text-gray-900"
                    >
                      <DocumentTextIcon
                        className="mr-3 shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      <span className="truncate">@{twitterUser?.username}</span>
                    </NavLink>
                  )}
                  {briefs.map(brief => (
                    <NavLink
                      key={brief.id}
                      to={`/dashboard/brief/${brief.id}`}
                      className="group flex items-center px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                      activeClassName="bg-gray-100 text-gray-900"
                    >
                      <DocumentTextIcon
                        className="mr-3 shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      <span className="truncate">{brief.name}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </nav>
          </div>

          {/*Bottom CTA location */}
          {/*<div className="shrink-0 block w-full ">*/}
          {/*  <div className="px-3 my-5"></div>*/}
          {/*</div>*/}
          <UpsellSidebarNotification account={account} />

          <div className="shrink-0 block w-full border-t border-gray-200 p-4">
            {/* User account dropdown */}
            <Menu as="div" className="relative inline-block text-left w-full">
              <MenuButton className="group w-full rounded-md  text-sm text-left font-medium text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-secondary">
                <span className="flex w-full justify-between items-center">
                  <span className="flex min-w-0 items-center justify-between space-x-3">
                    <img
                      className="w-10 h-10 bg-gray-300 rounded-full shrink-0"
                      src={account?.imageUrl}
                      alt=""
                    />
                    <span className="flex-1 flex flex-col min-w-0">
                      <span className="text-gray-900 text-sm font-medium truncate">
                        {account?.firstName} {account?.lastName}
                      </span>
                      <span className="text-gray-500 text-sm truncate">
                        {account?.email?.emailAddress}
                      </span>
                    </span>
                  </span>
                  <EllipsisVerticalIcon
                    className="shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </MenuButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="z-10 mx-3 origin-top absolute right-0 left-0 bottom-14 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black/5 divide-y divide-gray-200 focus:outline-hidden">
                  {isPwaCompatibleBrowser && (
                    <div className="py-1">
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            className={classNames(
                              focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                              'group flex items-center w-full px-4 py-2 text-sm'
                            )}
                            onClick={() => deferredPrompt?.prompt()}
                          >
                            <ArrowDownTrayIcon
                              className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                              aria-hidden="true"
                            />
                            Install App
                          </button>
                        )}
                      </MenuItem>
                    </div>
                  )}
                  <div className="py-1">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          className={classNames(
                            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                            'group flex items-center w-full px-4 py-2 text-sm'
                          )}
                          onClick={() => {
                            const my = window as any;
                            if (my.hasOwnProperty('Intercom')) {
                              my['Intercom']('show');
                            }
                          }}
                        >
                          <LifebuoyIcon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          Get Help
                        </button>
                      )}
                    </MenuItem>
                  </div>
                  <div className="py-1">
                    <MenuItem>
                      {({ focus }) => (
                        <Link
                          to={'/dashboard/settings/overview'}
                          className={classNames(
                            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                            'group flex items-center px-4 py-2 text-sm'
                          )}
                        >
                          <AdjustmentsHorizontalIcon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          Settings
                        </Link>
                      )}
                    </MenuItem>
                  </div>
                  <div className="py-1">
                    <MenuItem>
                      {({ focus }) => (
                        <Link
                          to={'/users/signout'}
                          className={classNames(
                            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                            'group flex items-center px-4 py-2 text-sm'
                          )}
                        >
                          <ArrowRightOnRectangleIcon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          Logout
                        </Link>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
